import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { carDataFormCSS } from './CarDataForm.css';
import { formatNumber } from '../../../../Utils';
import { montoDesdePct, pctPagoInicial } from '../../../../Utils/pagoInicial';
import { Button } from 'primereact/button';
import { Loader } from '../../../../Components';
import { Background } from './Background/Background';
import { CalculatorHeroIntro } from './components/CalculatorHeroIntro';
import { VehicleDetailsCard } from './components/VehicleDetailsCard';
import { FinancingOptionsCard } from './components/FinancingOptionsCard';
import { InsuranceCard } from './components/InsuranceCard';
import { QuoteResultsV2 } from './components/QuoteResultsV2';
// import { QuoteSummarySidebar } from './components/QuoteSummarySidebar';
// import { QuoteResultsPanel } from './components/QuoteResultsPanel';
import 'rsuite/dist/rsuite-no-reset.min.css';

function formatMoney(value) {
    const numericValue = Number(value);

    if (Number.isNaN(numericValue) || value === null || value === undefined) {
        return 'N/A';
    }

    return `$${formatNumber(numericValue.toFixed(2))}`;
}

// Firma de los campos que afectan la cotización; si cambia, hay que volver a cotizar.
function buildFormSignature(state) {
    return JSON.stringify({
        amount: state?.rodac?.amount,
        accesorios: state?.rodac?.accesorios,
        enganche: state?.rodac?.enganche,
        paymentCfg: state?.rodac?.payment_configuration_id,
        insurance: state?.rodac?.insurance,
        residual: state?.residualValue?.id,
        typeCoverage: state?.typeCoverage?.id,
        description: state?.rodac?.description_id,
        carStatus: state?.rodac?.car_status_id,
    });
}

function CarDataForm({
    state,
    setState,
    setStep,
    loading,
    plazoVal,
    setPlazoVal,
    plazoTimer,
    submit,
    loadInsurances,
    cleanErrors,
    setClass,
    sumAmountAndAccesories,
    typeRegime,
    residualValue,
    stateAmple,
    percentageDownPayment,
    hadleResidualValue,
    type_coverage,
    handleStatePlateChange,
    hideTypeCoverage = false,
    isCotizador3 = false,
    isAdmin = false,
    secret = null,
    isShared = false,
    hasPendingOverride = false,
    adminMode = false,
    insuranceSlot = null,
}) {
    const [submitting, setSubmitting] = useState(false);

    // En la recotización del dashboard de admin (/admin/dashboard) NO se muestran los
    // resultados públicos: solo se recalcula y se actualiza el budget. En cualquier otro
    // lugar (cotizador público, incluso con un admin con sesión abierta) sí se muestran.
    // `adminMode` hace lo mismo fuera de esa ruta (recotizar desde admin/usuarios).
    const location = useLocation();
    const isAdminDashboard = adminMode || location.pathname.startsWith('/admin/dashboard');

    // En /admin/dashboard: tras cotizar, deshabilitar "COTIZAR" hasta que el usuario
    // modifique algún valor del formulario (se compara una firma de los campos).
    const currentSignature = useMemo(() => buildFormSignature(state), [state]);
    const [quotedSignature, setQuotedSignature] = useState(null);
    const hasQuoteResults = !!(state?.pdfPlazo && state.pdfPlazo.length > 0);
    useEffect(() => {
        // Cuando llega una nueva cotización (cambia pdfPlazo), guardamos la firma con la que se hizo.
        if (hasQuoteResults) setQuotedSignature(currentSignature);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasQuoteResults, state?.pdfPlazo]);
    // Si hay un override de tasa pendiente, el botón debe quedar habilitado para poder aplicarlo.
    const alreadyQuoted = isAdminDashboard && quotedSignature !== null && quotedSignature === currentSignature && !hasPendingOverride;

    if (!state) {
        return <Loader />;
    }

    // --- Computed values ---
    const vehicleTotal = sumAmountAndAccesories();
    const totalIsReady = !Number.isNaN(vehicleTotal) && vehicleTotal > 0;
    const currentPaymentConfig = state.rodac.payment_configurations.find(item => +item.id === +state.rodac.payment_configuration_id);
    const currentTerm = +currentPaymentConfig?.configuration || +plazoVal || 0;
    const downPaymentAmount = Number(state.rodac.enganche) || 0;
    const currentDownPaymentPercentage = totalIsReady
        ? Math.max(
            Number(percentageDownPayment.min || 0),
            Math.min(
                Number(percentageDownPayment.max || 0),
                Math.round((downPaymentAmount / vehicleTotal) * 100)
            )
        )
        : Number(percentageDownPayment.min || 0);
    const downPaymentMin = Number(percentageDownPayment.min || 0);
    const downPaymentMax = Number(percentageDownPayment.max || 0);
    // % real del monto (1 decimal si no es entero) para el encabezado; la barra usa el entero limitado.
    const downPaymentPctLabel = totalIsReady ? pctPagoInicial(downPaymentAmount, vehicleTotal) : downPaymentMin;

    const insuranceDisabled = !(
        state.rodac.description_id !== null &&
        state.rodac.car_type_id !== null &&
        state.rodac.car_status_id !== null &&
        state.rodac.compensation_id !== null &&
        state.rodac.amount !== null &&
        state.rodac.zip !== null &&
        state.rodac.use_type_id !== null &&
        state.rodac.deductible_id !== null &&
        state.rodac.payment_configuration_id !== null &&
        state.rodac.package_id !== null &&
        state.rodac.colonia !== null &&
        state.rodac.municipio !== null &&
        state.rodac.ciudad !== null &&
        state.rodac.estado !== null &&
        state.rodac.subchannel_id !== null
    ) || state.rodac.fetched_insurances;

    // --- State updaters ---
    const updateRodac = (changes, shouldResetInsurance = true) => {
        setState({
            ...state,
            rodac: {
                ...state.rodac,
                ...changes,
                ...(shouldResetInsurance
                    ? {
                        fetched_insurances: false,
                        insurance: null,
                        insurances: [],
                        plazosInsurances: []
                    }
                    : {})
            }
        });
    };

    // --- Event handlers ---
    const handleNameChange = (event) => {
        cleanErrors('name');
        updateRodac({
            name: event.target.value === '' ? null : event.target.value.toUpperCase()
        }, false);
    };

    const handleTypeCoverageChange = (event) => {
        cleanErrors('type_coverage');
        setState({
            ...state,
            typeCoverage: {
                typeCoverageName: event.value,
                id: event.value.id,
            },
        });
    };

    const handleTypeEngineChange = (event) => {
        cleanErrors('type_engine');
        setState({
            ...state,
            typeEngine: {
                typeEngineName: event.value,
                id: event.value.id,
            },
        });
    };

    const handleTypeRegimeChange = (event) => {
        cleanErrors('typeRegime');
        setState({
            ...state,
            typeRegime: {
                id: event.target.value.id === '' ? null : event.target.value.id,
                typeRegimeName: event.value,
            },
        });
    };

    const handlePlazoChange = (value) => {
        setPlazoVal(value);
        const selectedConfig = state.rodac.payment_configurations.find(obj => obj.configuration == value);

        if (plazoTimer.current) {
            clearTimeout(plazoTimer.current);
        }

        plazoTimer.current = setTimeout(() => {
            updateRodac({
                payment_configuration_id: selectedConfig?.id || state.rodac.payment_configuration_id,
            });
        }, 300);
    };

    const handleDownPaymentChange = (value) => {
        // montoDesdePct redondea al peso y nunca deja el monto debajo del mínimo.
        const newAmount = montoDesdePct({ pct: value, base: vehicleTotal, minPct: downPaymentMin, maxPct: downPaymentMax });
        updateRodac({ enganche: newAmount }, false);
    };

    // Monto escrito a mano; FinancingOptionsCard ya lo redondeó y limitó.
    const handleDownPaymentAmountChange = (monto) => {
        updateRodac({ enganche: monto }, false);
    };

    const handleResidualChange = (value) => {
        const selectedOption = residualValue.find(item => parseInt(item.percentage) === value);
        cleanErrors('residualValue');
        setState({
            ...state,
            residualValue: selectedOption,
        });
    };

    const handleInsuranceChange = (value) => {
        cleanErrors('insurance');
        updateRodac({ insurance: value === '' ? null : value }, false);
    };

    const handleCotizar = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            const result = submit();
            if (result && typeof result.then === 'function') {
                await result.catch(() => {
                    setTimeout(() => {
                        const firstError = document.querySelector('.DropdownError, .TextInputError, .SliderError');
                        if (firstError) {
                            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    }, 50);
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            css={carDataFormCSS()}
            style={{
                display: (!secret || isShared) ? 'block' : 'none'
            }}
        >
            <Background />
            <div className='MainContainer'>
                <div className='LayoutShell'>
                    <div className='ContentColumn'>
                        <CalculatorHeroIntro
                            isAdmin={isAdmin}
                            name={state.rodac.name}
                            typeCoverageName={state.typeCoverage.typeCoverageName}
                            typeCoverageOptions={type_coverage}
                            hideTypeCoverage={hideTypeCoverage}
                            setClass={setClass}
                            onNameChange={handleNameChange}
                            onTypeCoverageChange={handleTypeCoverageChange}
                        />
                        <VehicleDetailsCard
                            rodac={state.rodac}
                            typeCoverage={state.typeCoverage}
                            typeEngine={state.typeEngine}
                            typeRegimeState={state.typeRegime}
                            typeRegimeOptions={typeRegime}
                            stateAmple={stateAmple}
                            setClass={setClass}
                            cleanErrors={cleanErrors}
                            updateRodac={updateRodac}
                            onTypeEngineChange={handleTypeEngineChange}
                            onTypeRegimeChange={handleTypeRegimeChange}
                            onStatePlateChange={handleStatePlateChange}
                        />
                        <FinancingOptionsCard
                            currentTerm={currentTerm}
                            plazoVal={plazoVal}
                            paymentConfigurations={state.rodac.payment_configurations}
                            currentDownPaymentPercentage={currentDownPaymentPercentage}
                            downPaymentPctLabel={downPaymentPctLabel}
                            downPaymentMin={downPaymentMin}
                            downPaymentMax={downPaymentMax}
                            totalIsReady={totalIsReady}
                            enganche={state.rodac.enganche}
                            vehicleTotal={vehicleTotal}
                            residualValue={residualValue}
                            residualOptions={hadleResidualValue()}
                            typeCoverageId={state.typeCoverage.typeCoverageName?.id}
                            residualPercentage={state.residualValue?.percentage}
                            setClass={setClass}
                            formatMoney={formatMoney}
                            onPlazoChange={handlePlazoChange}
                            onDownPaymentChange={handleDownPaymentChange}
                            onDownPaymentAmountChange={handleDownPaymentAmountChange}
                            onResidualChange={handleResidualChange}
                        />
                        <InsuranceCard
                            insurances={state.rodac.insurances}
                            selectedInsurance={state.rodac.insurance}
                            insuranceDisabled={insuranceDisabled}
                            loadInsurances={loadInsurances}
                            isLoading={state.loader}
                            divideAmountBy={parseFloat(state.plazos.parray[state.plazos.currentIndex])}
                            onInsuranceChange={handleInsuranceChange}
                        />
                        {insuranceSlot}
                        <div className='SubmitRow'>
                            <Button
                                label={alreadyQuoted ? 'COTIZADO' : (submitting ? 'Cotizando...' : 'COTIZAR')}
                                icon={alreadyQuoted ? 'pi pi-check' : undefined}
                                className='Green SubmitButton'
                                disabled={loading || submitting || alreadyQuoted}
                                loading={submitting}
                                onClick={handleCotizar}
                            />
                        </div>
                        {alreadyQuoted && (
                            <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--pei-text-helper, #64748b)', display: 'flex', alignItems: 'center', gap: 6 }}>
                                <i className="pi pi-check-circle" style={{ color: 'var(--pei-green-true, #00BA6E)' }} />
                                Cotización aplicada. Modifica algún valor para volver a cotizar.
                            </div>
                        )}
                        {/* En /admin/dashboard (recotización de un budget ya aceptado) NO se muestran
                            los resultados públicos; solo se recalcula y actualiza el budget. */}
                        {!isAdminDashboard && (
                            <QuoteResultsV2
                                state={state}
                                onRequest={() => setStep && setStep(2)}
                                isCotizador3={isCotizador3}
                            />
                        )}
                    </div>
                </div>
            </div>
            {loading && <Loader />}
        </div>
    );
}

export { CarDataForm };
